import { JWTRole } from "@hackthenorth/north";

import { Route } from "src/shared/constants/route";
import {
  useEventContext,
  useHackerContext,
  usePermissionsContext,
  useUserContext,
} from "src/shared/contexts";
import { HackerStage } from "src/shared/contexts/HackerContext/types";
import { canAccessHackerPostRsvpTools } from "src/shared/utils/hackerPostRsvpAccess";
import { canAccessHardware } from "src/shared/utils/hardwareAccess";
import { canAccessJudging } from "src/shared/utils/judgingAccess";
import { canAccessTravelSupportBot } from "src/shared/utils/travelSupportAccess";

import { SECTIONS, TSidebarSection } from "./sections";
import { canViewSidebarItem } from "./visibility";

export const useSidebarContent = () => {
  const { hasPermission } = usePermissionsContext();
  const { roles, isOrganizer, email } = useUserContext();
  const { stage } = useHackerContext();
  const { hackerPostRsvpTools, travelSupportBot, hardware, judging } =
    useEventContext();
  const statusOnlyHacker =
    !isOrganizer &&
    roles.includes(JWTRole.HACKER) &&
    !!stage &&
    [
      HackerStage.WAITLISTED,
      HackerStage.NOT_ACCEPTED,
      HackerStage.WITHDRAWN,
    ].includes(stage);
  const canAccessPostRsvpTools = canAccessHackerPostRsvpTools(
    roles,
    isOrganizer,
    hackerPostRsvpTools.isOpen
  );
  const canAccessTravelSupport = canAccessTravelSupportBot(
    roles,
    isOrganizer,
    travelSupportBot.enabled
  );
  const canAccessHardwareSection = canAccessHardware(
    roles,
    isOrganizer,
    hardware.isOpen,
    email,
    hardware.volunteersWithAccess
  );
  const canAccessJudgingSection = canAccessJudging(
    roles,
    isOrganizer,
    judging.isOpen
  );

  const sections: TSidebarSection[] = SECTIONS.map((section) => {
    return {
      ...section,
      content: section.content.filter((item) =>
        canViewSidebarItem(item, {
          isOrganizer,
          statusOnlyHacker,
          canAccessPostRsvpTools,
          canAccessTravelSupport,
          canAccessHardware: canAccessHardwareSection,
          canAccessJudging: canAccessJudgingSection,
          hasPermission,
        })
      ),
    };
  }).filter((section) => section.content.length > 0);

  return {
    sections: statusOnlyHacker
      ? sections.map((section) => ({
          ...section,
          content: section.content.map((item) => ({
            ...item,
            name: item.href === Route.HACKER_RSVP ? "Status" : item.name,
          })),
        }))
      : sections,
  };
};
